import FieldLabel from '@components/Common/FieldLabel';
import type { SelectProps } from '@mui/material';
import { FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import React from 'react';
import './index.scss';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectFormFieldProps {
  // Core props
  name: string;
  label: string;
  options: SelectOption[];
  value?: string | number;
  onChange: (name: string, value: string | number) => void;

  // Form integration
  formData?: Record<string, any>;
  errors?: Record<string, string>;

  // Validation
  required?: boolean;
  disabled?: boolean;
  error?: boolean;
  helperText?: string;

  // Layout
  gridProps?: {
    xs?: number;
    sm?: number;
    md?: number;
    lg?: number;
    xl?: number;
  };

  // Custom styling
  className?: string;
  variant?: 'outlined' | 'filled' | 'standard';
  size?: 'small' | 'medium';
  fullWidth?: boolean;

  // Additional Select props passthrough
  selectProps?: Partial<SelectProps>;
}

const SelectFormField: React.FC<SelectFormFieldProps> = ({
  name,
  label,
  options,
  value,
  onChange,
  formData,
  errors,
  required = false,
  disabled = false,
  error,
  helperText,
  gridProps,
  className = 'text-field',
  variant = 'outlined',
  size = 'medium',
  fullWidth = true,
  selectProps = {},
}) => {
  // Auto-validation: derive error and helperText from `errors` when present.
  // Also prefer an explicit `value` prop over `formData[name]` when resolving the select value.
  const autoError = (errors && typeof errors[name] !== 'undefined') ? !!errors[name] : !!error;
  const autoHelperText = (errors && typeof errors[name] !== 'undefined') ? errors[name] : helperText;
  const autoValue = typeof value !== 'undefined' ? value : (formData ? formData[name] : '');

  // Handle value changes
  const handleChange = (event: any) => {
    const newValue = event.target.value;
    onChange(name, newValue);
  };

  const selectField = (
    <div className="select-form-field">
      {/* Guarded: WorkspaceField renders this with an empty label, and an
          empty <label> still takes its margin. */}
      {label && (
        <FieldLabel id={`${name}-label`} required={required} disabled={disabled} htmlFor={name}>{label}</FieldLabel>
      )}
      {disabled ? (
        <Typography
          className="disabled-view-typography"
          variant="body1"
        >
          {(() => {
            const selectedOption = options.find(option => option.value === autoValue);
            return selectedOption ? selectedOption.label : (autoValue || '-');
          })()}
        </Typography>
      ) : (
        <FormControl
          className={className}
          disabled={disabled}
          error={autoError}
          fullWidth={fullWidth}
          size={size}
          variant={variant}
        >
          <Select
            disabled={disabled}
            error={autoError}
            id={name}
            /*
             * The label's htmlFor points at this id, but MUI puts the id on a <div role="combobox">,
             * which a <label> cannot name. So no select in the console had an accessible name: a
             * screen reader announced "combobox" and nothing else. labelId sets aria-labelledby on
             * the combobox, which does name it.
             */
            labelId={label ? `${name}-label` : undefined}
            name={name}
            required={required}
            value={autoValue || ''}
            onChange={handleChange}
            displayEmpty={options.some(o => o.value === '')}
            {...selectProps}
          >
            {options.map((option) => (
              <MenuItem
                key={option.value}
                disabled={option.disabled}
                value={option.value}
              >
                {option.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      {autoHelperText && !disabled && (
        <Typography
          className={`helper-text ${autoError ? 'error' : ''}`}
          variant="caption"
        >
          {autoHelperText}
        </Typography>
      )}
    </div>
  );

  // If gridProps are provided, wrap in Grid item
  if (gridProps) {
    return (
      <Grid {...gridProps}>
        {selectField}
      </Grid>
    );
  }

  return selectField;
};

export default SelectFormField;
